var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });
import {
  ConditionOperatorType,
  CandleValueType,
  IndicatorSeriesType,
  PositionOrderType,
  MarginMode,
  PositionType,
  AutoMode,
  NotifierType,
  InvestMode,
  OrderType,
  InvestListType,
  TriggerType,
  AverageUnitType,
  UpDown,
  EditMode,
  CandleComputedType,
  OperandSourceType,
  TaFunctionId
} from "./enum.js";
import {
  AvailableExchanges,
  AvailableTimeframes,
  DEFAULT_BACK_SIGNAL_HOURS,
  DEFAULT_BACKTEST_HOURS,
  DEFAULT_DONT_ORDER_AFTER_ERROR_MINUTES,
  DEFAULT_PRICE_RANGE_LIMIT,
  DEFAULT_INDICATOR_BUFFER_CANDLES,
  MIN_BACK_SIGNAL_HOURS,
  MAX_BACKTEST_HOURS,
  MAX_BACK_SIGNAL_HOURS,
  MIN_BACKTEST_HOURS,
  MIN_INDICATOR_BUFFER_CANDLES,
  MAX_INDICATOR_BUFFER_CANDLES,
  DEFAULT_VIRTUAL_BALANCE,
  DEFAULT_LEVERAGE
} from "./constant.js";
import { z } from "zod/v4";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc.js";
import {
  ShowLineOptionZodType,
  ShowColorOptionZodType,
  ColorOptionZodType,
  yAccessorValueZodType,
  AltLineOptionZodType
} from "./shape.js";
dayjs.extend(utc);
const AppConfigZodType = z.object({
  version: z.number(),
  /** FrontEnd 호스트: express 접속 허용 cors origin 등록 시 사용됨. */
  webHost: z.union([z.string(), z.string().array()]),
  /** 언어 설정: 한국어('ko') 또는 영어('en'). 기본값은 한국어. */
  locale: z.enum(["ko", "en"]).optional().default("ko")
});
const UpdateConditionRangeZodType = z.object({
  name: z.string(),
  action: z.enum(["enter", "exit", "match"]),
  time: z.number().optional()
  // unix timestamp 형식. 생략 시 서버에서 Date.now() 사용
});
const ExternalRpcMessageZodType = z.object({
  /** tRPC procedure 경로 (예: "invest.start", "invest.exitPositions") */
  procedure: z.string(),
  /** procedure의 input (Zod 스키마는 tRPC에서 자동 검증) */
  input: z.any().optional(),
  /** 요청 ID (응답 매칭용, 옵션) */
  requestId: z.string().optional()
});
const NotifierTargetBaseZodType = z.object({
  type: z.enum(NotifierType),
  apiToken: z.string(),
  eventType: z.union([z.literal(""), z.enum(OrderType)]).optional()
});
const TelegramNotifierZodType = NotifierTargetBaseZodType.extend({
  type: z.literal(NotifierType.TELEGRAM),
  chatIds: z.array(z.number()).optional()
});
const DiscordNotifierZodType = NotifierTargetBaseZodType.extend({
  type: z.literal(NotifierType.DISCORD)
});
const NotifierTargetZodType = z.discriminatedUnion("type", [TelegramNotifierZodType, DiscordNotifierZodType]);
const NotifierConfigZodType = z.object({
  version: z.number(),
  targets: NotifierTargetZodType.array()
});
const LoginZodType = z.object({
  botId: z.number(),
  email: z.string(),
  password: z.string().min(4).max(12),
  rememberEmail: z.boolean().optional().default(false),
  rememberPassword: z.boolean().optional().default(false)
});
const LicenseLoginZodType = z.object({
  license_id: z.number()
});
const LicenseLogoutZodType = z.object({
  license_id: z.number().optional(),
  live_id: z.number().optional()
});
const ExchangeApiKeySecretZodType = z.object({
  apiKey: z.string().optional(),
  secret: z.string().optional()
});
const ExchangeConfigZodType = z.object({
  id: z.enum(AvailableExchanges),
  /** testnet 사용 여부. */
  testMode: z.boolean().optional(),
  apiKeySecret: ExchangeApiKeySecretZodType,
  /** 메인 프로세스에서 사용할 프록시 IP */
  proxy: z.string().optional(),
  /** FrontEnd 용 1개 + 워커 프로세스에서 사용할 프록시 IP 목록 */
  proxyList: z.string().array().default([]),
  /**
   * Hedge 모드 활성화 여부.
   * - true: Hedge 모드 (롱/숏 동시 보유 가능, positionSide 필수)
   * - false: One-way 모드 (단일 방향만 보유 가능, positionSide='BOTH')
   * 기본값: true
   */
  hedgeMode: z.boolean().default(true),
  /** 펀딩비 정산 시각 (UTC 시 단위). 미설정 시 [0, 8, 16] 사용 */
  fundingTimes: z.number().array().optional()
});
const MarketConfigZodType = z.object({
  symbol: z.string(),
  /** 모니터링만하고 거래는 하지 않는 종목인 경우 true. 추세 참조만하는 종목인 경우. */
  watchOnly: z.boolean().optional(),
  // auto: z.enum(AutoMode),
  /** 할당 비율. 현물 거래소에서만 사용 */
  // distPercent: z.number(),
  // 레버리지 기본값 1
  leverage: z.number().optional().default(DEFAULT_LEVERAGE)
});
const MarketConfigMapZodType = z.record(z.string(), MarketConfigZodType);
const MarketConfigMapRecordsZodType = z.object({
  version: z.number(),
  [InvestListType.REALTIME]: MarketConfigMapZodType,
  [InvestListType.BACKTEST]: MarketConfigMapZodType
});
function isPositionTriggerMatchInfo(triggerMatchInfo) {
  return "orderId" in triggerMatchInfo;
}
__name(isPositionTriggerMatchInfo, "isPositionTriggerMatchInfo");
const isTicker = /* @__PURE__ */ __name((ohlcvOrTicker) => {
  return "change" in ohlcvOrTicker;
}, "isTicker");
const isOHLCV = /* @__PURE__ */ __name((ohlcv) => {
  return ohlcv[1] !== void 0;
}, "isOHLCV");
const OperandInputZodType = z.object({
  type: z.enum(OperandSourceType),
  /** 참조 대상 식별자 — indicator.id 또는 shape.id (둘 다 number) */
  sourceId: z.number().optional(),
  output: z.string().optional(),
  candleOffset: z.number().optional(),
  candleOffsetEnd: z.number().optional()
});
const ConditionOperandInputZodType = OperandInputZodType.extend({
  formulaParams: z.record(z.string(), OperandInputZodType).optional(),
  value: z.number().optional(),
  valueOffset: z.number().optional().default(0),
  valueOffsetByPercent: z.boolean().optional().default(false),
  strokeStyle: z.string()
});
const ConditionZodType = z.object({
  id: z.number(),
  name: z.string(),
  /** 이 조건이 적용되는 종목 */
  symbol: z.string().optional(),
  // type: z.enum(PositionOrderType),
  /** 조건 그룹. 이 그룹에 속한 조건들끼리는 AND 비교. 그룹과 그룹 사이는 OR 비교 */
  group: z.string().optional(),
  timeframe: z.enum(AvailableTimeframes),
  /** 비교 연산자. */
  operator: z.enum(ConditionOperatorType),
  operands: ConditionOperandInputZodType.array(),
  /** 연속 매칭 최소 횟수 */
  consecutiveMin: z.number().optional().nullable(),
  /** 연속 매칭 최대 횟수 */
  consecutiveMax: z.number().optional().nullable(),
  /** 용도 배열. 최소 1개 이상 선택 필수 **/
  usages: z.number().array().min(1),
  /** 비활성 여부 */
  disabled: z.boolean().optional(),
  // 2024-01-11 buruss 사용하지 않음
  // fillStyle: z.string().optional(),
  editMode: z.enum(EditMode).optional()
});
const IndicatorSeriesBaseZodType = z.object({
  displayName: z.string().optional()
});
const IndicatorLineSeriesConfigZodType = IndicatorSeriesBaseZodType.extend({
  type: z.literal(IndicatorSeriesType.LINE),
  yAccessor: yAccessorValueZodType,
  // 단일값
  line: ShowLineOptionZodType
  // { show, strokeStyle, strokeWidth, strokeDasharray }
});
const IndicatorBarSeriesBaseZodType = IndicatorSeriesBaseZodType.extend({
  yAccessor: yAccessorValueZodType,
  widthRatio: z.number()
});
const IndicatorBarSeriesConfigZodType = IndicatorBarSeriesBaseZodType.extend({
  type: z.literal(IndicatorSeriesType.BAR),
  fill: ShowColorOptionZodType
});
const IndicatorAltBarSeriesConfigZodType = IndicatorBarSeriesBaseZodType.extend({
  type: z.literal(IndicatorSeriesType.ALTBAR),
  fills: z.tuple([ShowColorOptionZodType, ShowColorOptionZodType]),
  fillMode: z.enum(["both", "plus", "minus"]).optional(),
  /** 색상 결정 기준: 'value' = 값이 0보다 큰지 (기본), 'candle' = 캔들이 양봉인지 */
  colorBy: z.enum(["value", "candle"]).optional()
});
const IndicatorAreaSeriesConfigZodType = IndicatorSeriesBaseZodType.extend({
  type: z.literal(IndicatorSeriesType.AREA),
  yAccessors: z.tuple([yAccessorValueZodType, yAccessorValueZodType]),
  // [0]: line1, [1]: line2
  fill: ShowColorOptionZodType,
  // { show, color }
  line: ShowLineOptionZodType.optional()
  // 기존 데이터 호환성을 위해 일단 optional, 추후에 삭제
});
const IndicatorAltAreaSeriesConfigZodType = IndicatorSeriesBaseZodType.extend({
  type: z.literal(IndicatorSeriesType.ALTAREA),
  yAccessors: z.tuple([yAccessorValueZodType, yAccessorValueZodType]),
  // [0]: line1, [1]: line2
  fills: z.tuple([ShowColorOptionZodType, ShowColorOptionZodType])
  // [0]: line1>line2, [1]: line1<line2
});
const IndicatorCandleSeriesConfigZodType = IndicatorSeriesBaseZodType.extend({
  type: z.literal(IndicatorSeriesType.CANDLE),
  yAccessors: z.tuple([yAccessorValueZodType, yAccessorValueZodType, yAccessorValueZodType, yAccessorValueZodType]),
  // [0]: open, [1]: high, [2]: low, [3]: close
  fills: z.tuple([ColorOptionZodType, ColorOptionZodType])
  // [0]: up, [1]: down (show 없음)
});
const IndicatorAltLineSeriesConfigZodType = IndicatorSeriesBaseZodType.extend({
  type: z.literal(IndicatorSeriesType.ALTLINE),
  yAccessor: yAccessorValueZodType,
  altLine: AltLineOptionZodType
});
const IndicatorSeriesConfigZodType = z.union([
  IndicatorLineSeriesConfigZodType,
  IndicatorAreaSeriesConfigZodType,
  IndicatorAltAreaSeriesConfigZodType,
  IndicatorBarSeriesConfigZodType,
  IndicatorAltBarSeriesConfigZodType,
  IndicatorCandleSeriesConfigZodType,
  IndicatorAltLineSeriesConfigZodType
]);
const IndicatorOutputZodType = z.object({
  show: z.boolean().optional().default(true),
  value: z.number().optional(),
  hidden: z.boolean().optional()
});
const CandleValueTypeZod = z.nativeEnum(CandleValueType);
const CandleComputedTypeZod = z.nativeEnum(CandleComputedType);
const CandleConditionTypeZod = z.union([CandleValueTypeZod, CandleComputedTypeZod]);
const IndicatorConfigZodType = z.object({
  id: z.number(),
  /** 참조하는 TaLib 함수 id. -2이면 Ta Function 대신 아래 formula 사용 */
  funcId: z.enum(TaFunctionId),
  funcName: z.string().optional(),
  name: z.string(),
  /** 이 지표가 소속될 timeframe */
  timeframe: z.enum(AvailableTimeframes),
  inputs: z.record(z.string(), z.union([CandleConditionTypeZod, z.number(), z.boolean(), z.string()])),
  /** 출력값 이름의 선 색상 */
  outputs: z.record(z.string(), IndicatorOutputZodType),
  outOptions: z.record(z.string(), z.union([z.number(), z.string(), z.boolean()]).optional()).optional(),
  series: IndicatorSeriesConfigZodType.array().optional(),
  newPanel: z.union([z.boolean(), z.literal("never"), z.literal("always")]).optional(),
  disabled: z.boolean().optional(),
  editMode: z.enum(EditMode).optional(),
  dualScale: z.boolean().optional()
});
const RoeStepZodType = z.object({
  /** 순익률 기준은 0이 아니어야 함 */
  change: z.number().positive().or(z.number().negative()),
  /** 금액 비율 퍼센트 */
  amount: z.number().min(0).max(100)
});
const TriggerBaseZodType = z.object({
  enabled: z.boolean()
});
const SignalStepZodType = z.object({
  // signalTypeId 는 양의 정수여야 함.
  signalTypeId: z.number().positive().int(),
  /** 금액 비율 퍼센트 */
  amount: z.number().min(0).max(100)
});
const SignalTriggerZodType = TriggerBaseZodType.extend({
  type: z.union([z.literal(TriggerType.SIGNAL_ENTER), z.literal(TriggerType.SIGNAL_EXIT)]),
  target: z.enum(PositionType),
  steps: SignalStepZodType.array()
});
const TrailingStepZodType = z.object({
  // change 는 양수여야 함.
  change: z.number().positive(),
  amount: z.number().min(0).max(100)
});
const TrailingExitTriggerZodType = TriggerBaseZodType.extend({
  type: z.literal(TriggerType.TRAILING_EXIT),
  target: z.union([z.enum(PositionType), z.literal("session")]),
  steps: TrailingStepZodType.array()
});
const ExclusiveExitTriggerZodType = TriggerBaseZodType.extend({
  type: z.literal(TriggerType.EXCLUSIVE_EXIT),
  /** 구간 분할 단계 */
  target: z.union([z.enum(PositionType), z.literal("session")]),
  steps: RoeStepZodType.array(),
  trailingSteps: TrailingStepZodType.array()
});
const OverlappingStepZodType = z.object({
  [UpDown.DOWN]: RoeStepZodType,
  [UpDown.UP]: RoeStepZodType
});
const OverlappingExitTriggerZodType = TriggerBaseZodType.extend({
  type: z.literal(TriggerType.OVERLAPPING_EXIT),
  target: z.union([z.enum(PositionType), z.literal("session")]),
  steps: OverlappingStepZodType.array(),
  trailingSteps: TrailingStepZodType.array()
});
const TriggerUnionZodType = z.union([
  SignalTriggerZodType,
  TrailingExitTriggerZodType,
  ExclusiveExitTriggerZodType,
  OverlappingExitTriggerZodType
]);
const StrategyConfigBaseZodType = z.object({
  id: z.string(),
  // 2024-05-08 lghjoa 값을 uuid 로 변경해야하므로 number => string 으로 변경
  name: z.string(),
  createdAt: z.date().optional(),
  // 2024-05-08 lghjoa 추가 (StrategyConfig 에서 이동)
  updatedAt: z.date().optional(),
  // 2024-05-08 lghjoa 추가 (StrategyConfig 에서 이동)
  updatedCount: z.number().optional().default(0),
  // 2024-05-08 lghjoa 추가 (StrategyConfig 에서 이동)
  remark: z.string().optional(),
  // 2024-05-08 lghjoa 추가 (StrategyConfig 에서 이동)
  /** 수정할 때마다 1씩 증가하는 버전 정보 */
  version: z.number().optional().default(0)
});
const TrendReferenceMapZodType = z.record(
  z.string(),
  z.object({
    targetTrend: z.enum(PositionType),
    editMode: z.enum(EditMode).optional()
  })
);
const TrendReferenceZodType = z.object({
  symbol: z.string(),
  targetTrend: z.enum(PositionType)
});
const SignalTypeZodType = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().optional(),
  editMode: z.enum(EditMode).optional()
});
const StrategyConfigZodType = StrategyConfigBaseZodType.extend({
  schemaVersion: z.number(),
  isDirty: z.boolean().optional(),
  /**
   * 빈 전략(draft) 표시. transient 플래그로 JSON 직렬화에서 제외된다.
   * cache-only 적재 정책: getDefault()에서 true로 부여, save() INSERT 후 false로 갱신.
   * (isDirty와 동일 패턴)
   */
  isDraft: z.boolean().optional(),
  /** 모니터링할 봉 단위들 */
  timeframes: z.array(z.enum(AvailableTimeframes)),
  /** 지표들 */
  indicators: IndicatorConfigZodType.array(),
  /** 신호들 */
  signalTypes: SignalTypeZodType.array(),
  /** 조건들 */
  conditions: ConditionZodType.array(),
  /** 참조 추세 종목 */
  references: z.record(z.enum(PositionOrderType), TrendReferenceMapZodType),
  /** 진입/청산 트리거들 */
  triggers: z.object({
    /** 신호 진입 */
    [TriggerType.SIGNAL_ENTER]: z.record(z.enum(PositionType), SignalTriggerZodType),
    /** 신호 청산 */
    [TriggerType.SIGNAL_EXIT]: z.record(z.enum(PositionType), SignalTriggerZodType),
    /** 추적 청산 */
    [TriggerType.TRAILING_EXIT]: z.record(
      z.union([z.enum(PositionType), z.literal("session")]),
      TrailingExitTriggerZodType
    ),
    /** 구간 청산 */
    [TriggerType.EXCLUSIVE_EXIT]: z.record(
      z.union([z.enum(PositionType), z.literal("session")]),
      ExclusiveExitTriggerZodType
    ),
    /** 단계 청산 */
    [TriggerType.OVERLAPPING_EXIT]: z.record(
      z.union([z.enum(PositionType), z.literal("session")]),
      OverlappingExitTriggerZodType
    )
  })
});
const InvestConfigZodType = z.object({
  id: z.number(),
  version: z.number(),
  name: z.string(),
  exchangeId: z.union([z.enum(AvailableExchanges), z.literal("")]),
  // spare: z.number(),
  /** !! deprecated !! 투자비율 사용 안 함. 100%를 기본값으로 사용 */
  investPercent: z.number(),
  distPercent: z.number().min(0).max(100),
  /** 평가 씨드 금액을 기준으로 종목당 배분 비율을 결정할지 여부 */
  distByEvaluatedSeed: z.boolean().optional().default(false),
  /** 같은 캔들안에서 돌파/진입/진출 체크 여부. 기본값 false */
  checkCrossInCandle: z.boolean(),
  /** 시세가 최근 진입가의 몇 *퍼센트* 이내이면 진입/청산하지 않기 */
  ignorePriceChangePercent: z.number().optional(),
  /** 가격 변동 % 미만 거래 제한 설정에 손절/익절은 제외 체크 */
  // allowCutIgnorePriceChange: z.boolean(),
  dontOrderAfterErrorMinutes: z.number().optional().default(DEFAULT_DONT_ORDER_AFTER_ERROR_MINUTES),
  simulateOrderAfterErrorCount: z.number().optional().default(0),
  marginMode: z.enum(MarginMode).optional(),
  /** leverage 삭제. MarketConfig 에서 leverage 구현함 */
  // leverage: z.number().optional(),
  // simulationMode: z.boolean().optional(),
  simulationBalance: z.number().optional().default(DEFAULT_VIRTUAL_BALANCE),
  backtestBalance: z.number().optional().default(DEFAULT_VIRTUAL_BALANCE),
  // defaultSeedPercent: z.number().min(0).max(100).default(DEFAULT_SEED_PERCENT),
  /** 선물에서 모든 symbol 을 모니터링 할지 여부 */
  monitorAllRealtimeSymbols: z.boolean().optional(),
  /** 투자 자동/수동 선택. 기본값 수동 */
  autoMode: z.enum(AutoMode),
  /** 사용할 전략 id */
  strategyId: z.string(),
  /** 평균 가격 계산을 위한 Tick 누적 수 또는 분(minute) 수 */
  averageCount: z.number().optional().default(1),
  /** 평균 인터벌 단위 */
  averageUnit: z.enum(AverageUnitType).optional().default(AverageUnitType.SECOND),
  /** 슬리피지 */
  // slippage: z.number().optional().default(DEFAULT_SLIPPAGE),
  /** 기본 백시그널 기간 (시간 단위) */
  defaultBackSignalHours: z.number().min(MIN_BACK_SIGNAL_HOURS).max(MAX_BACK_SIGNAL_HOURS).default(DEFAULT_BACK_SIGNAL_HOURS),
  /** 기본 백테스트 기간 (시간 단위) */
  defaultBacktestHours: z.number().min(MIN_BACKTEST_HOURS).max(MAX_BACKTEST_HOURS).default(DEFAULT_BACKTEST_HOURS),
  /** 진입 시 호가창의 가격 범위 한계 퍼센트 값. 0~1% 사이 실수로 지정. 기본값 0.1% */
  priceRangeLimit: z.number().min(0).max(1).optional().default(DEFAULT_PRICE_RANGE_LIMIT),
  /** 지표 정확도를 높이기 위한 여분 캔들 수 */
  indicatorBufferCandles: z.number().min(MIN_INDICATOR_BUFFER_CANDLES).max(MAX_INDICATOR_BUFFER_CANDLES).default(DEFAULT_INDICATOR_BUFFER_CANDLES),
  /** 목표 순익/손절 도달 시자동 청산 및 종료 */
  autoExitOnPnl: z.boolean().optional().default(false)
});
const SessionConfigInputZodType = z.object({
  investMode: z.enum(InvestMode),
  duration: z.object({
    startAt: z.coerce.date().optional(),
    endAt: z.coerce.date().optional()
  }),
  goalPnl: z.object({
    profit: z.number().min(0).optional(),
    loss: z.number().min(0).optional()
  }).optional()
});
const QueryInputZodType = z.object({
  sessionId: z.number(),
  investMode: z.enum(InvestMode),
  symbol: z.string().optional(),
  positionType: z.enum(PositionType).optional(),
  from: z.coerce.date().optional(),
  to: z.coerce.date().optional()
});
const PaginatedQueryInputZodType = QueryInputZodType.extend({
  skip: z.number().optional().default(0),
  take: z.number().optional().default(10)
});
const CorrelationCalculationOptionsZodType = z.object({
  baseSymbol: z.string(),
  targetSymbols: z.array(z.string()).min(1),
  // 기준과 비교할 종목들 (1개 이상)
  timeframe: z.enum(AvailableTimeframes),
  // UI에서 탭 timeframe 전달
  priceType: z.enum(["open-close", "low-high"]),
  valueType: z.enum(["price", "percentage"]),
  startAt: z.coerce.date(),
  // 봉 가져올 시작 시간
  endAt: z.coerce.date()
  // 봉 가져올 마지막 시간
});
const LeverageBracketZodType = z.object({
  bracket: z.number(),
  initialLeverage: z.number(),
  notionalCap: z.number(),
  notionalFloor: z.number(),
  maintMarginRatio: z.number(),
  cum: z.number()
});
const LeverageTiersZodType = z.object({
  symbol: z.string(),
  brackets: z.array(LeverageBracketZodType)
});
export {
  AppConfigZodType,
  CandleComputedTypeZod,
  CandleConditionTypeZod,
  CandleValueTypeZod,
  ConditionOperandInputZodType,
  ConditionZodType,
  CorrelationCalculationOptionsZodType,
  DiscordNotifierZodType,
  ExchangeApiKeySecretZodType,
  ExchangeConfigZodType,
  ExclusiveExitTriggerZodType,
  ExternalRpcMessageZodType,
  IndicatorAltAreaSeriesConfigZodType,
  IndicatorAltBarSeriesConfigZodType,
  IndicatorAltLineSeriesConfigZodType,
  IndicatorAreaSeriesConfigZodType,
  IndicatorBarSeriesConfigZodType,
  IndicatorCandleSeriesConfigZodType,
  IndicatorConfigZodType,
  IndicatorLineSeriesConfigZodType,
  IndicatorOutputZodType,
  IndicatorSeriesConfigZodType,
  InvestConfigZodType,
  LeverageBracketZodType,
  LeverageTiersZodType,
  LicenseLoginZodType,
  LicenseLogoutZodType,
  LoginZodType,
  MarketConfigMapRecordsZodType,
  MarketConfigMapZodType,
  MarketConfigZodType,
  NotifierConfigZodType,
  NotifierTargetBaseZodType,
  NotifierTargetZodType,
  OperandInputZodType,
  OverlappingExitTriggerZodType,
  OverlappingStepZodType,
  PaginatedQueryInputZodType,
  QueryInputZodType,
  RoeStepZodType,
  SessionConfigInputZodType,
  SignalStepZodType,
  SignalTriggerZodType,
  SignalTypeZodType,
  StrategyConfigBaseZodType,
  StrategyConfigZodType,
  TelegramNotifierZodType,
  TrailingExitTriggerZodType,
  TrailingStepZodType,
  TrendReferenceMapZodType,
  TrendReferenceZodType,
  TriggerUnionZodType,
  UpdateConditionRangeZodType,
  isOHLCV,
  isPositionTriggerMatchInfo,
  isTicker
};
//# sourceMappingURL=interface.js.map
